import isObject from 'isobject';

import {
  CURRENT_USER_INFO,
  PROFILE,
  FAVORITE_USERS,
  DASHBOARD,
  RESET_DASHBOARD,
  CHANGE_LAST_OTP_AT,
  SIGNUP,
  SEARCH_PHONE_NUMBERS,
  API_KEYS,
  UPDATE_API_KEY,
  DELETE_API_KEY,
  QUICK_FILTERS,
  SOA_REQUESTS,
  SOA_REQUEST,
  AUTH_IPS,
  USER_OFFICE_LOCATIONS,
  EMAIL_NOTIFICATIONS,
  COMPLETE_TOUR,
  CUSTOM_FIELDS,
  PATCH_CUSTOM_FIELD
} from '@/store/actions/currentUser.actions';
import { FONT_SIZE_DEFAULT, PRIMARY_COLOR_GREEN, BACKGROUND_DEFAULT } from '@/store/actions/theme.actions';

const initialState = {
  id: null,
  tos: null,
  updated_tos: null,
  username: null,
  roles: [],
  email: null,
  profile: {
    first_name: null,
    last_name: null,
    personal_photo: {},
    logo: {},
    font_size: FONT_SIZE_DEFAULT,
    primary_color: PRIMARY_COLOR_GREEN,
    background: BACKGROUND_DEFAULT,
    dashboard_layout: {}
  },
  signup: {},
  favorite_users: [],
  dashboard_info: {},
  available_phone_numbers: [],
  api_keys: [],
  quick_filters: [],
  soa_requests: [],
  soa_requests_meta: {},
  soa_request: {},
  auth_ips: [],
  auth_ips_meta: {},
  office_locations: [],
  office_locations_meta: {},
  email_notifications: [],
  custom_fields: []
};

export default function currentUser(state = initialState, action) {
  switch (action.type) {
    case CURRENT_USER_INFO:
      // Be defensive: payload or nested keys can be undefined during bootstrap or on errors
      const data = action?.payload?.data || {};
      const profile = data?.profile || {};
      return {
        ...state,
        ...data,
        ...accountType(profile?.account_type),
        ...smsSubscriptionType(data),
        ...dialerSubscriptionType(data),
        profile: {
          ...profile,
          personal_photo: profile.personal_photo || {},
          logo: profile.logo || {},
          font_size: profile.font_size || FONT_SIZE_DEFAULT,
          primary_color: profile.primary_color || PRIMARY_COLOR_GREEN,
          background: profile.background || BACKGROUND_DEFAULT,
          dashboard_layout: profile.dashboard_layout || {}
        }
      };
    case CHANGE_LAST_OTP_AT:
      return {
        ...state,
        last_otp_at: action.payload
      };
    case PROFILE:
      return {
        ...state,
        profile: action.payload.data
      };
    case FAVORITE_USERS:
      return {
        ...state,
        favorite_users: action.payload.data
      };
    case RESET_DASHBOARD:
      return {
        ...state,
        dashboard_info: {}
      };
    case DASHBOARD: {
      let dashboard_info = {};
      Object.entries({ ...action.payload, ...state.dashboard_info }).forEach(([key, value]) => {
        if (isObject(value)) {
          dashboard_info[key] = {
            ...value,
            ...action.payload[key]
          };
        } else {
          if (['client_birthdays', 'prospect_birthdays'].includes(key)) {
            dashboard_info[key] = [...(state.dashboard_info[key] || []), ...(action.payload[key] || [])];
          } else {
            dashboard_info[key] = value;
          }
        }
      });

      return {
        ...state,
        dashboard_info
      };
    }
    case SIGNUP:
      return {
        ...state,
        signup: action.payload.data
      };
    case SEARCH_PHONE_NUMBERS:
      return {
        ...state,
        available_phone_numbers: action.payload.data
      };
    case API_KEYS:
      return {
        ...state,
        api_keys: action.payload.data
      };
    case UPDATE_API_KEY:
      return {
        ...state,
        api_keys: state.api_keys.map(el => (el.id === action.payload.data.id ? action.payload.data : el))
      };
    case DELETE_API_KEY:
      return {
        ...state,
        api_keys: state.api_keys.filter(el => el.id !== action.payload.data.id)
      };
    case QUICK_FILTERS:
      return {
        ...state,
        quick_filters: action.payload.data
      };
    case SOA_REQUESTS:
      return {
        ...state,
        soa_requests: action.payload.data,
        soa_requests_meta: action.payload.meta
      };
    case SOA_REQUEST:
      return {
        ...state,
        soa_request: action.payload.data
      };
    case AUTH_IPS:
      return {
        ...state,
        auth_ips: action.payload.data,
        auth_ips_meta: action.payload.meta
      };
    case USER_OFFICE_LOCATIONS:
      return {
        ...state,
        office_locations: action.payload.data,
        office_locations_meta: action.payload.meta
      };
    case EMAIL_NOTIFICATIONS:
      return {
        ...state,
        email_notifications: action.payload.data.email_notifications || []
      };
    case COMPLETE_TOUR:
      return {
        ...state,
        completed_tours: { ...(state.completed_tours || {}), [action.payload]: true }
      };
    case CUSTOM_FIELDS:
      return {
        ...state,
        custom_fields: action.payload?.data ?? []
      };
    case PATCH_CUSTOM_FIELD:
      return {
        ...state,
        custom_fields: state.custom_fields.map(f => (f.id === action.payload.id ? { ...f, ...action.payload.body } : f))
      };
    default:
      return state;
  }
}

const accountType = type => {
  switch (type) {
    case 'sub_admin':
      return { sub_admin: true, admin: true };
    case 'pool_holder':
      return { pool_holder: true, basic: true };
    default:
      if (type) {
        return { [type]: true };
      } else {
        return {};
      }
  }
};

const smsSubscriptionType = data => {
  if (data.company_sms_subscription?.id) return { sms_subscription_type: 'company' };
  if (data.sms_subscription?.id) {
    if (data.sms_subscription.order_type === 'Subscription::ManagerPay') return { sms_subscription_type: 'manager' };
    if (data.sms_subscription.order_type === 'Subscription::SelfPay') return { sms_subscription_type: 'self' };
  }
  return {};
};

const dialerSubscriptionType = data => {
  if (data.company_dialer_subscription?.id) return { dialer_subscription_type: 'company' };
  if (data.dialer_subscription?.id) {
    if (data.dialer_subscription.order_type === 'Subscription::ManagerPay')
      return { dialer_subscription_type: 'manager' };
    if (data.dialer_subscription.order_type === 'Subscription::SelfPay') return { dialer_subscription_type: 'self' };
  }
  return {};
};
